/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import { ArrowRight, Compass, CircleDot, Triangle, Wind, MessageCircleHeart, Heart } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen whenua-gradient grain">
      {/* Header */}
      <header className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Logo />
          <div className="leading-tight">
            <div className="font-serif text-lg sm:text-xl text-ngahere">Build your whare</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground hidden sm:block">the over-arching kaupapa</div>
          </div>
        </div>
        <nav className="flex items-center gap-2">
          <Link
            to="/login"
            data-testid="header-login"
            className="text-sm px-4 py-2 rounded-full hover:bg-muka transition-colors text-charcoal"
          >
            Tomokia / Sign in
          </Link>
          <Link
            to="/signup"
            data-testid="header-signup"
            className="text-sm px-5 py-2 rounded-full bg-ngahere text-muka hover:bg-ngahere/90 transition-colors"
          >
            Tīmata
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-16 pb-16 sm:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 animate-fadeup">
            <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-6 flex items-center gap-2">
              <span className="w-8 h-px bg-kokowai" />
              He kaupapa mō ngā wāhine
            </div>
            <h1 className="font-serif italic text-5xl sm:text-6xl lg:text-7xl text-kokowai leading-[1.05] mb-6">
              Know thyself.
            </h1>
            <p className="font-serif text-2xl sm:text-3xl text-ngahere mb-1">
              Mā te mātau ki ora
            </p>
            <p className="text-base sm:text-lg italic text-charcoal/70 max-w-xl leading-relaxed mb-8">
              through knowing comes life.
            </p>
            <p className="text-base text-charcoal/70 max-w-xl leading-relaxed mb-8">
              A daily companion for wāhine walking the road home from prison.
              Not surveillance, not compliance — a mirror and witness to progress,
              navigating a pathway to success. A sister and a kuia in your pocket,
              helping you integrate, align, and identify a life that is yours.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/signup"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-ngahere text-muka hover:bg-ngahere/90 transition-all"
              >
                Start your kete
                <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to="/login"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-charcoal/20 hover:border-charcoal/40 text-charcoal transition-all"
              >
                Already walking
              </Link>
            </div>
            <div className="mt-10 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Whiria te muka aronga · weave the threads of awareness
            </div>
          </div>

          <div className="lg:col-span-5">
            <WhareMotif />
          </div>
        </div>
      </section>

      {/* Pou (pillars) */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-3">Ngā pou</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-charcoal mb-12 max-w-2xl">
          The whare at the centre is yourself. The triangle is how you show up. The wheel is the world you walk through. The whare gets built from within.
        </h2>
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
          <Pou icon={Compass} title="Te Whare Tapa Whā check-in" body="Daily four-wall wellbeing — tinana, hinengaro, wairua, whānau. No scoring. No shame. Just noticing." />
          <Pou icon={Triangle} title="The belief triangle" body="Belief is the apex. Without belief, motivation for change collapses. Belief aligned with action embodies identity. This can sustain change." />
          <Pou icon={CircleDot} title="The Reintegration Wheel" body="Map seven domains — housing, whānau, justice, finances, identity, community, education. See where the imbalance lives." />
          <Pou icon={Wind} title="Hā · coping toolkit" body="Box breathing, 5-4-3-2-1 grounding, urge-surfing, karakia. Real tools for the moments that bite." />
          <Pou icon={MessageCircleHeart} title="Tuahine + Whaea companion" body="Two voices, one kaupapa. A sister who has been inside, and a kuia who tells the truth. You choose who speaks today." />
        </div>
      </section>

      {/* Whakaaro */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-24">
        <div className="bg-muka rounded-3xl p-8 sm:p-12 border border-border shadow-[0_8px_30px_rgba(42,42,42,0.06)]">
          <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-4">Whakaaro</div>
          <p className="font-serif text-2xl sm:text-3xl text-charcoal italic leading-snug mb-4">
            "I built walls and shelter for shadows, before it all came crashing down and I found myself housed
            with 400 other women trying to understand the blueprint and rebuild my life."
          </p>
          <p className="text-sm text-muted-foreground">
            Built by wāhine, for wāhine. Founded in lived experience. Grounded in whakapapa.
          </p>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-4 sm:px-6 py-10 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
        <div>© Mā te mātau ki ora · Aotearoa</div>
        <div className="flex items-center gap-1">
          <Heart size={12} className="text-kokowai" /> KAAWAI · Keeping Aspirations Alive
        </div>
      </footer>
    </div>
  );
}

function Pou({ icon: Icon, title, body }) {
  return (
    <div className="bg-muka rounded-2xl p-6 sm:p-7 border border-border hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(42,42,42,0.08)] transition-all duration-300">
      <Icon size={22} strokeWidth={1.4} className="text-ngahere mb-4" />
      <h3 className="font-serif text-xl text-charcoal mb-2">{title}</h3>
      <p className="text-sm text-charcoal/70 leading-relaxed">{body}</p>
    </div>
  );
}

function Logo() {
  return (
    <svg width="36" height="36" viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <circle cx="20" cy="20" r="18" stroke="#2B4522" strokeWidth="1.2" />
      <path d="M20 4 L20 36" stroke="#8B3A33" strokeWidth="1" strokeDasharray="2 3" />
      <path d="M4 20 L36 20" stroke="#8B3A33" strokeWidth="1" strokeDasharray="2 3" />
      <path d="M8 8 L32 32 M32 8 L8 32" stroke="#9C6644" strokeWidth="0.8" opacity="0.6" />
      <circle cx="20" cy="20" r="3" fill="#2B4522" />
    </svg>
  );
}

function WhareMotif() {
  // Outer wheel + 7 spokes (the pou), inverted triangle (apex sits inside the circle),
  // whare centered at the heart.
  const cx = 200, cy = 200;
  const R = 150;       // outer wheel
  const Ri = 125;      // inner red dashed circle — top triangle points now sit on this
  // Inverted triangle — top points on the inner red circle, apex stays inside the circle
  const topL = [cx - Ri * Math.cos(Math.PI / 6), cy - Ri * Math.sin(Math.PI / 6)];
  const topR = [cx + Ri * Math.cos(Math.PI / 6), cy - Ri * Math.sin(Math.PI / 6)];
  const apex = [cx, cy + 115]; // inside the outer circle, near the bottom

  // The seven pou — new order
  const pillars = ["Identity", "Justice", "Housing", "Pūtea", "Whānau", "Community", "Education"];

  // BENEFITS / BEHAVIOURS labels — sit inside the upper triangle area
  const inward = 30;
  const dirL = Math.atan2(cy - topL[1], cx - topL[0]);
  const labelBenefits = [topL[0] + inward * Math.cos(dirL), topL[1] + inward * Math.sin(dirL)];
  const dirR = Math.atan2(cy - topR[1], cx - topR[0]);
  const labelBehaviours = [topR[0] + inward * Math.cos(dirR), topR[1] + inward * Math.sin(dirR)];

  return (
    <div className="relative aspect-square max-w-md mx-auto">
      <svg viewBox="-30 -10 460 420" className="w-full h-full">
        <defs>
          <radialGradient id="g1" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FAF8F5" />
            <stop offset="100%" stopColor="#F4F1EA" />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r="180" fill="url(#g1)" />
        {/* outer wheel */}
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="#2B4522" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r="125" fill="none" stroke="#8B3A33" strokeWidth="0.8" strokeDasharray="3 4" />
        {/* spokes */}
        {Array.from({ length: 7 }).map((_, i) => {
          const a = (i / 7) * Math.PI * 2 - Math.PI / 2;
          const x2 = cx + Math.cos(a) * R;
          const y2 = cy + Math.sin(a) * R;
          return <line key={i} x1={cx} y1={cy} x2={x2} y2={y2} stroke="#2B4522" strokeWidth="0.7" opacity="0.35" />;
        })}
        {/* inverted triangle of belief — apex sits inside the circle */}
        <path
          d={`M ${topL[0]} ${topL[1]} L ${topR[0]} ${topR[1]} L ${apex[0]} ${apex[1]} Z`}
          fill="rgba(139,58,51,0.04)"
          stroke="#8B3A33"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        {/* triangle vertex dots */}
        <circle cx={topL[0]} cy={topL[1]} r="5" fill="#8B3A33" />
        <circle cx={topR[0]} cy={topR[1]} r="5" fill="#8B3A33" />
        <circle cx={apex[0]} cy={apex[1]} r="5" fill="#8B3A33" />
        {/* BENEFITS / BEHAVIOURS sit just inside the top corners */}
        <text x={labelBenefits[0]} y={labelBenefits[1]} textAnchor="start" dominantBaseline="middle" fontFamily="Manrope" fontSize="10" letterSpacing="1.8" fill="#8B3A33">BENEFITS</text>
        <text x={labelBehaviours[0]} y={labelBehaviours[1]} textAnchor="end" dominantBaseline="middle" fontFamily="Manrope" fontSize="10" letterSpacing="1.8" fill="#8B3A33">BEHAVIOURS</text>
        {/* BELIEF sits below the apex of the triangle, inside the circle */}
        <text x={apex[0]} y={apex[1] + 18} textAnchor="middle" fontFamily="Manrope" fontSize="11" letterSpacing="2.5" fill="#8B3A33" fontWeight="500">BELIEF</text>
        {/* whare at the heart — clean house outline (no eaves sticking past the walls) */}
        <g transform={`translate(${cx},${cy + 6})`}>
          <path d="M -26 22 L -26 9 L 0 -26 L 26 9 L 26 22 Z" fill="none" stroke="#9C6644" strokeWidth="1.4" strokeLinejoin="round" />
          {/* two inner wall posts only */}
          <path d="M -9 22 L -9 -14 M 9 22 L 9 -14" stroke="#9C6644" strokeWidth="1" />
          <circle cx="0" cy="-26" r="3" fill="#9C6644" />
        </g>
        {/* the seven pou — dots on the wheel, names outside */}
        {pillars.map((name, i) => {
          const a = (i / 7) * Math.PI * 2 - Math.PI / 2;
          const dx = cx + Math.cos(a) * R;
          const dy = cy + Math.sin(a) * R;
          const labelR = R + 18;
          const lx = cx + Math.cos(a) * labelR;
          const ly = cy + Math.sin(a) * labelR;
          const c = Math.cos(a);
          const anchor = c > 0.3 ? "start" : c < -0.3 ? "end" : "middle";
          return (
            <g key={name}>
              <circle cx={dx} cy={dy} r="4.5" fill="#2B4522" />
              <text x={lx} y={ly} textAnchor={anchor} dominantBaseline="middle" fontFamily="Manrope" fontSize="11" fill="#2A2A2A">{name}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
